import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import es from './locales/es.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import de from './locales/de.json'
import no from './locales/no.json'
import pt from './locales/pt.json'
import ptBR from './locales/pt-BR.json'
import ru from './locales/ru.json'
import ro from './locales/ro.json'
import zh from './locales/zh.json'
import id from './locales/id.json'
import vi from './locales/vi.json'
import th from './locales/th.json'
import ka from './locales/ka.json'
import my from './locales/my.json'

const resources = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  it: { translation: it },
  de: { translation: de },
  no: { translation: no },
  pt: { translation: pt },
  'pt-BR': { translation: ptBR },
  ru: { translation: ru },
  ro: { translation: ro },
  zh: { translation: zh },
  id: { translation: id },
  vi: { translation: vi },
  th: { translation: th },
  ka: { translation: ka },
  my: { translation: my },
}

export const SUPPORTED_LANGUAGES: readonly string[] = Object.keys(resources)
export const LANGUAGE_STORAGE_KEY = 'i18n_language'

/** Base subtags browsers report for a language we ship under another code:
 * Norwegian Bokmål (`nb`, `nb-NO`) and Nynorsk (`nn`) → our `no`. */
const BASE_ALIASES: Readonly<Record<string, string>> = { nb: 'no', nn: 'no' }

/** The supported locale for a language tag: the full tag first (`pt-BR`),
 * then its base (`de-AT` → `de`, `nb-NO` → `no`), case-insensitively; null
 * when neither is one of the 16. */
export function matchSupportedLanguage(tag: string | null | undefined): string | null {
  if (!tag) return null
  const lower = tag.toLowerCase()
  const exact = SUPPORTED_LANGUAGES.find((l) => l.toLowerCase() === lower)
  if (exact) return exact
  const subtag = lower.split('-')[0]
  const base = BASE_ALIASES[subtag] ?? subtag
  return SUPPORTED_LANGUAGES.find((l) => l.toLowerCase() === base) ?? null
}

/** The starting language (MS-993): the reader's own choice, persisted by
 * LanguageMenu → the browser's languages in order → VITE_DEFAULT_LANGUAGE →
 * English. Same 16 languages and localStorage-only persistence as
 * mambu-sparks-studio (no backend-persisted locale; ARCHITECTURE_DECISIONS.md). */
export function initialLanguage(
  persisted: string | null,
  browser: readonly string[],
  fallback: string | undefined
): string {
  const fromPersisted = matchSupportedLanguage(persisted)
  if (fromPersisted) return fromPersisted
  for (const tag of browser) {
    const match = matchSupportedLanguage(tag)
    if (match) return match
  }
  return matchSupportedLanguage(fallback) ?? 'en'
}

function readPersistedLanguage(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return []
  if (navigator.languages?.length) return navigator.languages
  return navigator.language ? [navigator.language] : []
}

i18n.use(initReactI18next).init({
  lng: initialLanguage(readPersistedLanguage(), browserLanguages(), import.meta.env.VITE_DEFAULT_LANGUAGE),
  fallbackLng: 'en',
  resources,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
})

/** Keep the document in the active language: `<html lang>` (so screen
 * readers read the UI with the right language's rules) and the tab title
 * (the brand, `appShell.brand`) — index.html's static `lang="en"` and title
 * only stand until this runs. */
function syncDocumentLanguage(): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = i18n.language
  resetDocumentTitle()
}

/** Put the tab title back to the brand in the active language. A page that
 * sets its own title calls this when it unmounts (NotFound); while it is
 * mounted, its own effect re-sets the title after a language change, because
 * it depends on `t`. */
export function resetDocumentTitle(): void {
  document.title = i18n.t('appShell.brand')
}
syncDocumentLanguage()
i18n.on('languageChanged', syncDocumentLanguage)

export default i18n
